import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://gtbuyspreadsheet.run";
const logoUrl = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "What is GTBuy Spreadsheet? Complete Beginner Guide for Ecommerce Sellers",
  description: "Discover how the GTBuy Spreadsheet system revolutionizes product sourcing for online store owners. A step-by-step walkthrough of the structured spreadsheet method.",
  datePublished: "2026-07-01",
  dateModified: "2026-07-01",
  author: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    logo: {
      "@type": "ImageObject",
      url: logoUrl,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${siteUrl}/what-is-gtbuy-spreadsheet`,
  },
};

export default function BlogArticle() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />

      <main>
        {/* Article Header */}
        <section className="relative w-full pt-24 pb-16 md:pt-32 md:pb-20 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <Link
                to="/"
                className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-arrow-left-line"></i> Back to Home
              </Link>
              <span className="text-foreground-300">/</span>
              <span className="text-sm text-foreground-500">Guides</span>
            </div>

            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">
              Beginner Guide &middot; 8 min read
            </span>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight mb-4">
              What is GTBuy Spreadsheet? Complete Beginner Guide for Ecommerce Sellers
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl">
              Discover how the GTBuy Spreadsheet system revolutionizes product sourcing
              for online store owners. A step-by-step walkthrough of the structured
              spreadsheet method that helps you compare suppliers, track pricing, and
              scale your dropshipping business efficiently.
            </p>
            <p className="text-xs text-foreground-400 mt-4">
              Published: July 2026 &middot; Last updated: July 2026
            </p>
          </div>
        </section>

        {/* Header Image */}
        <section className="w-full px-6 md:px-10 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-[16/7] w-full rounded-2xl overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20desk%20workspace%20with%20laptop%20showing%20colorful%20spreadsheet%20charts%20and%20data%20analysis%20dashboard%20notebook%20with%20handwritten%20sourcing%20notes%20pen%20and%20ceramic%20coffee%20mug%20on%20light%20oak%20surface%20soft%20morning%20window%20light%20airy%20fresh%20editorial%20photography%20high%20detail%20warm%20professional%20composition&width=1600&height=700&seq=article-what-is-header-v2&orientation=landscape"
                alt="GTBuy Spreadsheet ecommerce sourcing system on laptop"
                title="GTBuy Spreadsheet — Structured Sourcing System for Ecommerce"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="w-full px-6 md:px-10 pb-16">
          <div className="max-w-3xl mx-auto">
            {/* Introduction */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">
              Why GTBuy Spreadsheet Exists
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              If you have ever run an online store, you know how chaotic product sourcing
              can get. You start with a handful of suppliers and a simple Excel sheet. Then
              your catalog grows to 50 SKUs. Then 200. Suddenly your spreadsheet is a
              sprawling mess of merged cells, broken formulas, and supplier notes scattered
              across five different tabs. You waste hours every week just trying to figure
              out which supplier has the best price for a restock order.
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              This is exactly the problem the <strong>GTBuy Spreadsheet</strong> system
              solves. It is not software you install or a subscription you pay for. It is a
              structured methodology &mdash; a way of organizing your sourcing data so that
              every supplier, every product variant, and every price quote lives in one
              clean, searchable, and comparable format. Think of it as a framework that
              turns your messy spreadsheets into a powerful sourcing command center.
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              The idea behind GTBuy is elegantly simple: <strong>structured data beats
              scattered notes every single time</strong>. When your supplier information
              follows a consistent format, comparing options becomes a matter of seconds
              rather than hours. When your pricing history is tracked systematically,
              you spot trends before your competitors do. When your entire sourcing
              workflow runs on one clean system, scaling from 50 to 500 products stops
              being terrifying and starts being exciting.
            </p>

            <p className="text-sm text-foreground-500 italic mb-8">
              Related:{" "}
              <Link to="/gtbuy-vs-kakobuy" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">GTBuy vs Kakobuy Comparison</Link>{" "}&middot;{" "}
              <Link to="/best-sourcing-tools" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Best Sourcing Tools</Link>
            </p>

            {/* How It Works */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">
              How the GTBuy Spreadsheet System Works
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-6">
              The system breaks down into four straightforward stages. Each stage builds on
              the previous one, creating a complete sourcing workflow that you can repeat
              for every product in your catalog.
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tl-lg">
                      Step
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200">
                      Action
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tr-lg">
                      Outcome
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-foreground-700 font-medium border-b border-background-200">
                      1
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Input product specifications and target price range
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Clear sourcing brief for each item
                    </td>
                  </tr>
                  <tr className="hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-foreground-700 font-medium border-b border-background-200">
                      2
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Match products with qualified suppliers and collect quotes
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Standardized comparison dataset
                    </td>
                  </tr>
                  <tr className="hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-foreground-700 font-medium border-b border-background-200">
                      3
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Compare pricing, MOQ, shipping, and lead times side by side
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Data-driven supplier selection
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-foreground-700 font-medium">
                      4
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700">
                      Place orders and track performance over time
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700">
                      Continuous improvement loop
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-base text-foreground-700 leading-relaxed mb-8">
              What makes this workflow powerful is its <strong>repeatability</strong>.
              Once you set up the spreadsheet structure once, every new product follows the
              same path. You stop reinventing your sourcing process for each item and start
              optimizing a system that works across your entire catalog.
            </p>

            {/* Mid Article Image */}
            <div className="aspect-[16/9] w-full rounded-xl overflow-hidden bg-background-200 mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=Ecommerce%20sourcing%20dashboard%20with%20supplier%20comparison%20table%20on%20laptop%20screen%20color%20coded%20price%20columns%20and%20performance%20metrics%20minimalist%20office%20setup%20with%20green%20monstera%20plant%20on%20light%20cream%20desk%20soft%20natural%20daylight%20clean%20modern%20aesthetic%20high%20detail%20editorial%20photography&width=800&height=500&seq=article-what-is-mid-v2&orientation=landscape"
                alt="Supplier comparison dashboard in spreadsheet system"
                title="GTBuy Spreadsheet — Supplier Comparison Dashboard"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            {/* Benefits */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">
              Key Benefits of Using the GTBuy Approach
            </h2>

            <h3 className="font-heading text-xl text-foreground-950 mt-8 mb-3">
              Faster Sourcing Decisions
            </h3>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              When five suppliers send you quotes in five different formats, comparing them
              manually is slow and error-prone. The GTBuy system enforces a uniform data
              structure, so you can stack supplier quotes next to each other and spot the
              best option instantly. What used to take half a day now takes 15 minutes.
            </p>

            <h3 className="font-heading text-xl text-foreground-950 mt-8 mb-3">
              Lower Sourcing Costs Through Visibility
            </h3>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              When your pricing data lives in a structured spreadsheet with historical
              records, you gain something invaluable: <strong>trend visibility</strong>.
              You can see which suppliers consistently offer the best prices, which ones
              have seasonal fluctuations, and where you might have room to negotiate. Most
              sellers using the GTBuy method report a meaningful reduction in per-unit costs
              within the first three months of adopting the system.
            </p>

            <h3 className="font-heading text-xl text-foreground-950 mt-8 mb-3">
              Scalable Operations
            </h3>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              The biggest killer of ecommerce growth is operational chaos. A store doing
              $5,000 a month can often get by with ad-hoc sourcing. But at $50,000 a month,
              the cracks show. The GTBuy Spreadsheet system is designed to scale &mdash;
              add 50 new products, and the same structure handles them without breaking.
              This scalability is why experienced sellers move toward structured systems
              as their businesses mature.
            </p>

            <p className="text-sm text-foreground-500 italic mb-4">
              Want to see the full product range available?{" "}
              <Link
                to="/"
                className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
              >
                Return to the GTBuy Spreadsheet homepage
              </Link>{" "}
              and browse all 14 product categories.
            </p>

            {/* Comparison */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">
              Spreadsheet Sourcing vs. Manual Methods
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-6">
              Many sellers start with a manual approach &mdash; emails, WhatsApp messages,
              and mental notes. Here is how the GTBuy system compares:
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tl-lg">
                      Aspect
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200">
                      Manual Sourcing
                    </th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tr-lg">
                      GTBuy Spreadsheet
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-foreground-700 font-medium border-b border-background-200">
                      Data Organization
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Scattered across chats, emails, memory
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Centralized, searchable, structured
                    </td>
                  </tr>
                  <tr className="hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-foreground-700 font-medium border-b border-background-200">
                      Comparison Speed
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Hours of manual cross-referencing
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Minutes with structured tables
                    </td>
                  </tr>
                  <tr className="hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-foreground-700 font-medium border-b border-background-200">
                      Error Rate
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      High &mdash; easy to miss price differences
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Low &mdash; formulas auto-calculate
                    </td>
                  </tr>
                  <tr className="hover:bg-background-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-foreground-700 font-medium border-b border-background-200">
                      Scalability
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Breaks beyond 20-30 products
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">
                      Handles 500+ products comfortably
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-foreground-700 font-medium">
                      Historical Tracking
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700">
                      Nearly impossible without notes
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground-700">
                      Built-in with versioned records
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Getting Started */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">
              How to Get Started with GTBuy Spreadsheet
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              Getting started does not require any special software or technical skills.
              If you can use a basic spreadsheet application like Google Sheets or
              Microsoft Excel, you already have everything you need. The key is setting
              up the right structure and committing to using it consistently.
            </p>
            <ol className="list-decimal list-inside space-y-3 mb-8 text-base text-foreground-700 leading-relaxed">
              <li>
                <strong>Create your master template</strong> with columns for product name,
                SKU, supplier name, unit price, MOQ, shipping cost, lead time, and quality
                notes. This becomes your single source of truth.
              </li>
              <li>
                <strong>Populate existing supplier data</strong> into the template. Even
                if the data is incomplete, getting it all into one place is the first win.
              </li>
              <li>
                <strong>Set up comparison sheets</strong> for products where you have
                multiple supplier options. Use simple formulas to highlight the best
                price and fastest shipping.
              </li>
              <li>
                <strong>Establish a review cadence</strong> &mdash; weekly or biweekly
                check-ins to update prices, add new suppliers, and archive outdated quotes.
              </li>
            </ol>

            <p className="text-sm text-foreground-500 italic mb-4">
              Looking for fashion products to source?{" "}
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-primary-600 hover:text-primary-700 underline cursor-pointer"
              >
                Visit KA Spreadsheet
              </a>{" "}
              for a wide selection of shoes, clothing, and accessories ready for your store.
            </p>

            {/* Conclusion */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">
              Is GTBuy Spreadsheet Right for Your Business?
            </h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              If you are running an ecommerce store with more than 20 products and sourcing
              from multiple suppliers, the answer is almost certainly yes. The time you
              invest in setting up a structured sourcing system pays for itself within
              weeks through faster decisions, fewer ordering mistakes, and better supplier
              negotiations.
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              The GTBuy Spreadsheet approach is not about adding complexity to your
              business. It is about <strong>removing the hidden complexity</strong> that
              already exists in your scattered emails, chat messages, and mental notes.
              By bringing everything into one clean, structured format, you free up mental
              energy to focus on what actually grows your store: finding great products
              and selling them to happy customers.
            </p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">
              Ready to explore the full catalog of products available for your store?
              Head back to the{" "}
              <Link
                to="/"
                className="text-primary-600 hover:text-primary-700 font-medium underline cursor-pointer"
              >
                GTBuy Spreadsheet homepage
              </Link>{" "}
              and browse 14 curated categories &mdash; from Shoes and Hoodies to
              Accessories and Perfumes. Every category links directly to KA Spreadsheet
              where you can view detailed product listings and start building your
              sourcing pipeline.
            </p>

            <p className="text-sm text-foreground-500 italic mb-4">
              Further reading:{" "}
              <Link to="/gtbuy-spreadsheet-review" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">GTBuy Review 2026</Link>{" "}&middot;{" "}
              <Link to="/how-to-use-gtbuy" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">How to Use GTBuy</Link>{" "}&middot;{" "}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Homepage</Link>
            </p>
          </div>
        </article>

        {/* Bottom CTA */}
        <section className="relative w-full py-20 md:py-28 px-6 md:px-10 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url(https://readdy.ai/api/search-image?query=Soft%20pastel%20fashion%20accessories%20silk%20scarves%20and%20delicate%20jewelry%20arranged%20artfully%20on%20light%20cream%20linen%20surface%20with%20laptop%20displaying%20spreadsheet%20in%20soft%20focus%20background%20minimalist%20composition%20natural%20window%20light%20fresh%20airy%20editorial%20photography%20high%20detail%20feminine%20aesthetic&width=1600&height=600&seq=article-what-is-cta-v2&orientation=landscape)",
            }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/35"></div>
          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl text-background-50 mb-4">
              Start Sourcing Smarter Today
            </h2>
            <p className="text-background-50/80 text-base md:text-lg max-w-lg mx-auto mb-8 leading-relaxed">
              Visit KA Spreadsheet to explore thousands of products across 14 categories.
              Apply the GTBuy structured sourcing method and transform how you manage your
              ecommerce supply chain.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                GTBuy Sheet &mdash; Shop Now
              </a>
              <Link
                to="/"
                className="px-6 py-3 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 font-medium text-sm hover:bg-background-50/30 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Homepage
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}